// src/components/MobileTabBar.jsx
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PrefetchLink from "./PrefetchLink";
import { useState, useEffect } from "react";
import { AuthService } from "../services/auth";

const ALL_TABS = [
  {
    key: "home",
    label: <b>Accueil</b>,
    path: "/",
    requiresAuth: false,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <path d="M3 10.5 12 3l9 7.5" />
        <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
      </svg>
    ),
  },
  {
    key: "achat",
    label: <b>Achat</b>,
    path: "/achat",
    requiresAuth: false,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <path d="M6 6h15l-2 8H7L6 6Z" />
        <circle cx="9" cy="19" r="1.5" />
        <circle cx="18" cy="19" r="1.5" />
      </svg>
    ),
  },
  {
    key: "location",
    label: <b>Location</b>,
    path: "/location",
    requiresAuth: true,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <circle cx="12" cy="12" r="6" />
        <path d="M12 8v4l2 2" />
      </svg>
    ),
  },
  {
    key: "offers",
    label: <b>Mes offres</b>,
    path: "/mes-annonces",
    requiresAuth: true,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <path d="M6 4h12v16l-6-3-6 3V4Z" />
      </svg>
    ),
  },
  {
    key: "add",
    label: <b>Ajouter</b>,
    path: "/ajouter",
    requiresAuth: true,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <path d="M12 5v14M5 12h14" />
      </svg>
    ),
  },
  {
    key: "profile",
    label: <b>Profil</b>,
    path: "/profil",
    requiresAuth: true,
    icon: (active) => (
      <svg viewBox="0 0 24 24" className={`w-6 h-6 ${active ? "stroke-blue-700" : "stroke-gray-500"}`} fill="none" strokeWidth="1.8">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20a8 8 0 0 1 16 0" />
      </svg>
    ),
  },
];

export default function MobileTabBar({ visibleKeys, className = "" }) {
  const { pathname } = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem("auth_token")
  );

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setIsAuthenticated(false);
        return;
      }
      try {
        await AuthService.me();
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem("auth_token");
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    const handleStorage = (e) => {
      if (e.key === "auth_token") {
        setIsAuthenticated(!!e.newValue);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [pathname]);

  const keys = visibleKeys && visibleKeys.length ? visibleKeys : ALL_TABS.map((t) => t.key);

  const tabs = ALL_TABS.filter((t) => {
    if (!keys.includes(t.key)) return false;
    if (!isAuthenticated && t.requiresAuth) return false;
    return true;
  });

  const isActive = (path) => {
    if (path === "/") return pathname === "/";
    return pathname === path || pathname.startsWith(path + "/");
  };

  return (
    <nav className={`fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t md:hidden ${className}`}>
      <div
        className="relative h-[68px] px-1 flex items-center justify-between"
        style={{ paddingBottom: "var(--safe-bottom)" }}
      >
        {tabs.map((t) => {
          const active = isActive(t.path);
          return (
            <PrefetchLink
              key={t.key}
              to={t.path}
              className="relative flex-1 h-full flex flex-col items-center justify-center text-[11px]"
            >
              <motion.div whileTap={{ scale: 0.94 }}>
                {t.icon(active)}
              </motion.div>
              <span className={`mt-1 ${active ? "text-blue-700 font-medium" : "text-gray-500"}`}>
                {t.label}
              </span>

              {active && (
                <motion.span
                  layoutId="tab-underline"
                  className="absolute bottom-0 w-10 h-0.5 bg-blue-700 rounded-full"
                />
              )}
            </PrefetchLink>
          );
        })}
      </div>
    </nav>
  );
}